import React, { FC, PropsWithChildren, useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@material-ui/core';
import { Alert, AlertTitle } from '@material-ui/lab';

enum LNAPermissionState {
    GRANTED = 'granted',
    DENIED = 'denied',
    PROMPT = 'prompt',
    UNKNOWN = 'unknown',
    ERROR = 'error'
}

export const LocalNetworkAccessGate: FC<PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
    const [permissionState, setPermissionState] = useState<LNAPermissionState>(LNAPermissionState.UNKNOWN);
    const [requestPermission, setRequestPermission] = useState<boolean>(false);

    useEffect(() => {
        const queryPermission = async () => {
            try {
                const status = await navigator.permissions.query({ name: 'local-network-access' as any });
                // prompt/granted/denied
                setPermissionState(status.state as LNAPermissionState);
            } catch (error) {
                setPermissionState(LNAPermissionState.ERROR);

            }
        };
        queryPermission();
    }, []);

    // wait for permission check
    if (permissionState === LNAPermissionState.UNKNOWN) {
        return (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="15vh">
                <CircularProgress />
            </Box>
        );
    }

    // user was not prompted yet, describe user what to do next and then trigger prompt by rendering actual content
    // (e.g. private iframe)
    if (permissionState === LNAPermissionState.PROMPT && !requestPermission) {
        return (
            <Box maxWidth={560} marginLeft="auto" marginRight="auto" marginTop={2}>
                <Alert severity="warning" style={{
                    marginLeft: 'auto',
                    marginRight: 'auto'
                }} >
                    <AlertTitle>Lokaler Netzwerkzugriff benötigt</AlertTitle>
                    Dieser Dienst benötigt die Berechtigung für den Zugriff auf das lokale Netzwerk.
                    Bitte gewähren Sie die Berechtigung im Browser-Popup.
                </Alert>
                <Button variant="contained" color="primary" style={{
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'block',
                    marginTop: 8
                }} onClick={() => {
                    setRequestPermission(true);
                }}>
                    Berechtigung anfordern
                </Button>
            </Box>
        );
    }

    if (permissionState === LNAPermissionState.DENIED) {
        return (
            <Box maxWidth={560} marginLeft="auto" marginRight="auto" marginTop={2}>
                <Alert severity="error">
                    <AlertTitle>Lokaler Netzwerkzugriff blockiert</AlertTitle>
                    Der lokale Netzwerkzugriff ist für diese Seite blockiert.
                    Bitte passe deine Browser-Einstellungen an und lade diese Seite neu.
                    <br />
                    <br />
                    Chrome Einstellungen <b>&gt;</b> Datenschutz und Sicherheit <b>&gt;</b> Website-Einstellungen <b>&gt;</b> Zusätzliche Berechtigungen <b>&gt;</b> Zugriff auf lokales Netzwerk
                    <br />
                    <br />
                    Suche nach dieser Seite und wähle für &quot;<b>Zugriff auf lokales Netzwerk</b>&quot; &quot;<b>Nachfragen (Standard)</b>&quot; aus.
                </Alert>
                <Button variant="contained" color="primary" style={{
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'block',
                    marginTop: 8
                }} onClick={() => {
                    window.location.reload();
                }}>
                    Seite neu laden
                </Button>
            </Box>
        );
    }

    // rendering content:
    // - permission granted (user allowed local network traffic)
    // - permission needs to be prompted and user confirmed (this will render content triggering the browser prompt)
    // - permission query resulted in error (browser does not support permission API) -> content should render fine
    return (<>{children}</>);
};
